import { MultiSelectCreatable, MultiSelectOption } from "@/components/misc/Multiselect";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Song } from "@/javascript/library-objects";
import { Classroom } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import { Box, Button, Flex, Loader, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext, useEffect, useState } from "react";
import ConductionModal from "../ConductionModal";

interface SongClassroomAssignmentFormProps {
	song: Song;
	onClose: () => void;
	onAssigned: () => void;
}

const SongClassroomAssignmentForm: React.FC<SongClassroomAssignmentFormProps> = ({ song, onClose, onAssigned }) => {
	const [classrooms, setClassrooms] = useState<Classroom[] | null>([]);
	const [loadingClassrooms, setLoadingClassrooms] = useState<boolean>(true);

	const auth = useContext(AuthContext);
	if (!auth) {
		throw new Error("AdminSongAssignmentForm must be a child of AuthContext.");
	}
	const token = auth.token;
	if (!token) return null;

	// Set up the form with initial values and validations.
	const form = useForm({
		initialValues: {
			classrooms: [] as MultiSelectOption[],
		}
	});

	const fetchClassrooms = () => {
		const notificationId = Utils.displayNotification(
			"Loading Classrooms",
			"Fetching all classrooms, please wait...",
			"blue",
			"loading"
		);

		ConductionAPI.getAllClassrooms(token)
			.then((loadedClassroomsInfo: Classroom[] | null) => {
				if (!loadedClassroomsInfo || loadedClassroomsInfo.length === 0) {
					setClassrooms(null);
					Utils.updateNotification(
						notificationId,
						"No Classrooms Found",
						"No classrooms are currently available.",
						"red",
						<IconAlertCircle size={16} />,
						true
					);
				} else {
					form.values.classrooms = loadedClassroomsInfo
						.filter(c => c.songIds.includes(song.songId))
						.map((classroom) => {
							const option: MultiSelectOption = {
								label: classroom.name,
								value: classroom.id
							};
							return option;
						})
					setClassrooms(
						loadedClassroomsInfo.sort(
							(a, b) =>
								b.dateCreated.getTime() - a.dateCreated.getTime() // Newest first
						)
					);
					Utils.updateNotification(
						notificationId,
						"Classrooms Loaded",
						"All classrooms have been retrieved.",
						"green",
						<IconCheck size={16} />
					);
				}
			})
			.catch(() => {
				Utils.updateNotification(
					notificationId,
					"Error Fetching Classrooms",
					"An error occurred while retrieving classrooms.",
					"red",
					<IconAlertCircle size={16} />,
					true
				);
			})
			.finally(() => setLoadingClassrooms(false));
	}

	useEffect(() => {
		fetchClassrooms();
	}, [])

	const handleSubmit = async () => {
		if (!form.isValid()) {
			form.validate();
			return;
		}

		// Display a notification that deletion is in progress.
		const notificationId = Utils.displayNotification(
			"Assigning Song",
			undefined,
			"blue",
			"loading"
		);

		const classroomIds: string[] = form.values.classrooms.map((classroom) =>
			classroom.value.toString()
		)

		try {
			await ConductionAPI.updateSongAssignment(song.songId, classroomIds, token);

			Utils.updateNotification(
				notificationId,
				"Assigned Song",
				undefined,
				"green",
				<IconCheck size={16} />
			);
			onAssigned();
			onClose();
		} catch (error) {
			console.error("Error assigning song", error);
			Utils.updateNotification(
				notificationId,
				"Error",
				"Failed to assign song.",
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		}
	};

	const classroomOptions: MultiSelectOption[] = classrooms?.map((classroom) => ({
		label: classroom.name,
		value: classroom.id
	})) ?? []

	const composers = song?.artists.filter(a => a.artistType === "Composer") ?? []

	const height = 20; // view height (vh)

	return (
		<ConductionModal
			onClose={onClose}
			title={`Assign Song "${song.songName}" to Classrooms`}
			size="md"
		>
			<Box
				style={{
					minHeight: `${height}vh`
				}}
			>
				{
					(loadingClassrooms) ? (
						<Loader size="xl" variant="dots" />
					) : song === null ? (
						<Text>Song data did not load correctly.</Text>
					) : (
						<form onSubmit={form.onSubmit(handleSubmit)}>
							<Flex
								direction={"column"}
								justify={"space-between"}
								align={"stretch"}
								style={{
									minHeight: `${height}vh`
								}}
							>
								<Stack>
									<Stack>
										<Text fw={500}>Which classrooms should have "{song.songName}" by {composers.map((a, i) => `${composers.length > 1 && i === composers.length - 1 ? "and " : ""}${a.artistName}`).join(composers.length > 2 ? ", " : " ")} </Text>
										<MultiSelectCreatable
											options={classroomOptions}
											value={form.values.classrooms}
											creatable={false}
											{...form.getInputProps("classrooms")}
										/>
									</Stack>
								</Stack>
								<Button
									type="submit"
									radius="xl"
									size='sm'
									loading={form.submitting}
									color="red"
								>
									Assign Song
								</Button>
							</Flex>
						</form>
					)
				}
			</Box>
		</ConductionModal>
	);
};

export default SongClassroomAssignmentForm;
